/**
 * @file AvitoFinanceSettingsTab.tsx
 * @description Таб «Настройки» (phase14.0.4): retention / archive.
 *
 * Авто-fetch (autoFetchEnabled / autoFetchPeriodDays) — заготовка под
 * подфазу phase14.0.4.X, на phase14.0.4 отображаются только инфо.
 */

import { Loader2, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Alert } from "../../../../components/ui/alert";
import { Button } from "../../../../components/ui/button";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ApiError } from "../../../../lib/api-client";
import {
  shopAvitoFinance,
  type AvitoFinanceSettingsDto,
} from "../../../../lib/api-shop-avito-finance";

export function AvitoFinanceSettingsTab() {
  const notify = useNotify();
  const [settings, setSettings] = useState<AvitoFinanceSettingsDto | null>(null);
  const [historyRetentionDays, setHistory] = useState<string>("365");
  const [archiveOlderThanDays, setArchive] = useState<string>("180");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await shopAvitoFinance.getSettings();
      setSettings(s);
      setHistory(String(s.historyRetentionDays));
      setArchive(String(s.archiveOlderThanDays));
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось загрузить настройки.");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSave(): Promise<void> {
    const retention = Number.parseInt(historyRetentionDays, 10);
    const archive = Number.parseInt(archiveOlderThanDays, 10);
    if (Number.isNaN(retention) || retention < 30 || retention > 3650) {
      notify.error("Срок хранения: от 30 до 3650 дней.");
      return;
    }
    if (Number.isNaN(archive) || archive < 7 || archive > 3650) {
      notify.error("Срок архивации: от 7 до 3650 дней.");
      return;
    }
    if (archive >= retention) {
      notify.error("Срок архивации должен быть МЕНЬШЕ срока хранения.");
      return;
    }
    setBusy(true);
    try {
      const updated = await shopAvitoFinance.updateSettings({
        historyRetentionDays: retention,
        archiveOlderThanDays: archive,
      });
      setSettings(updated);
      notify.success("Настройки сохранены.");
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <Loader2 className="size-5 animate-spin text-on-surface-variant mx-auto my-8" />;
  }

  return (
    <div className="flex flex-col gap-4 max-w-xl">
      <EditorialCard>
        <EditorialCard.Header
          title="Хранение операций"
          subtitle="Архивация — soft (запись остаётся, не показывается по умолчанию). Удаление — hard."
        />
        <EditorialCard.Body className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="archive">Архивировать операции старше (дней)</Label>
            <Input
              id="archive"
              type="number"
              min="7"
              max="3650"
              value={archiveOlderThanDays}
              onChange={(e) => setArchive(e.target.value)}
            />
            <p className="text-xs text-on-surface-variant">
              По умолчанию: 180. Архивные не отображаются в таблице операций без флага «Включая архивные».
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="retention">Удалять операции старше (дней)</Label>
            <Input
              id="retention"
              type="number"
              min="30"
              max="3650"
              value={historyRetentionDays}
              onChange={(e) => setHistory(e.target.value)}
            />
            <p className="text-xs text-on-surface-variant">
              По умолчанию: 365. Удаляются ТОЛЬКО уже архивные. Должно быть больше срока архивации.
            </p>
          </div>

          <Button onClick={onSave} disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Сохранить
          </Button>
        </EditorialCard.Body>
      </EditorialCard>

      <EditorialCard>
        <EditorialCard.Header title="Автоматическое получение" />
        <EditorialCard.Body>
          <Alert severity="info">
            Авто-fetch через cron — следующая подфаза (после оценки реальных объёмов).
            Сейчас все данные тянутся только вручную через кнопку «Получить с Avito».
            Текущая настройка autoFetchEnabled = {settings?.autoFetchEnabled ? "true" : "false"}.
          </Alert>
        </EditorialCard.Body>
      </EditorialCard>
    </div>
  );
}
